/**
 * Dismissals Slice
 *
 * Redux slice for managing dismissed cards in JotAudit feature.
 * Uses redux-persist to remember dismissals across sessions.
 *
 * Pattern follows: /src/features/system-notifications/store/slice.ts
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

// ============================================================================
// Types
// ============================================================================

export interface DismissalsState {
  dismissedCardIds: string[]; // Persisted to localStorage
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: DismissalsState = {
  dismissedCardIds: [],
};

// ============================================================================
// Slice
// ============================================================================

export const dismissalsSlice = createSlice({
  name: 'jotaudit/dismissals',
  initialState,
  reducers: {
    /**
     * Dismiss a card by ID (persisted via redux-persist)
     */
    dismissCard: (state, action: PayloadAction<string>) => {
      if (!state.dismissedCardIds.includes(action.payload)) {
        state.dismissedCardIds.push(action.payload);
      }
    },

    /**
     * Restore a dismissed card (for testing/debugging)
     */
    restoreCard: (state, action: PayloadAction<string>) => {
      state.dismissedCardIds = state.dismissedCardIds.filter(
        id => id !== action.payload
      );
    },

    /**
     * Clear all dismissals (for admin reset)
     */
    clearAllDismissals: state => {
      state.dismissedCardIds = [];
    },
  },
});

// ============================================================================
// Exports
// ============================================================================

export const { dismissCard, restoreCard, clearAllDismissals } =
  dismissalsSlice.actions;

export default dismissalsSlice.reducer;
